import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaChartLine, FaGlobeAfrica, FaDatabase, FaUsers, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import API_URL from '../config';

function Home() {
  const [stats, setStats] = useState({
    population: null,
    gdp: null,
    literacy: null,
    lifeExpectancy: null
  });

  useEffect(() => {
    // Fetch latest population
    fetch(`${API_URL}/api/population`)
      .then(res => res.json())
      .then(result => {
        if (result.status === 'success' && result.data.length > 0) {
          setStats(prev => ({ ...prev, population: result.data[0].total_population }));
        }
      }).catch(() => {});

    // Fetch latest economy
    fetch(`${API_URL}/api/economy`)
      .then(res => res.json())
      .then(result => {
        if (result.status === 'success' && result.data.length > 0) {
          setStats(prev => ({ ...prev, gdp: result.data[0].gdp_growth }));
        }
      }).catch(() => {});

    // Fetch latest health
    fetch(`${API_URL}/api/health`)
      .then(res => res.json())
      .then(result => {
        if (result.status === 'success' && result.data.length > 0) {
          setStats(prev => ({ ...prev, lifeExpectancy: result.data[0].life_expectancy }));
        }
      }).catch(() => {});

    // Fetch latest education
    fetch(`${API_URL}/api/education`)
      .then(res => res.json())
      .then(result => {
        if (result.status === 'success' && result.data.length > 0) {
          setStats(prev => ({ ...prev, literacy: result.data[0].literacy_rate }));
        }
      }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-900 via-indigo-800 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block bg-indigo-700 text-indigo-200 text-sm font-medium px-4 py-1 rounded-full mb-6">
              🇰🇪 Kenya National Statistics Platform
            </span>
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              Kenya's Data,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                Simplified
              </span>
            </h1>
            <p className="text-xl text-indigo-200 mb-10 max-w-3xl mx-auto leading-relaxed">
              Access national statistics on population, economy, health, and education
              in one place. No more digging through PDFs or scattered reports.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/population">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-indigo-700 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-indigo-50 transition flex items-center gap-2"
                >
                  Explore Data <FaArrowRight />
                </motion.button>
              </Link>
              <Link to="/compare">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-indigo-700 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-indigo-600 transition border border-indigo-500"
                >
                  Compare Statistics
                </motion.button>
              </Link>
            </div>
          </motion.div>

          {/* Live Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16"
          >
            <StatBadge
              label="Population"
              value={stats.population ? `${stats.population}M` : '...'}
              icon="👥"
            />
            <StatBadge
              label="GDP Growth"
              value={stats.gdp ? `${stats.gdp}%` : '...'}
              icon="📈"
            />
            <StatBadge
              label="Life Expectancy"
              value={stats.lifeExpectancy ? `${stats.lifeExpectancy} yrs` : '...'}
              icon="❤️"
            />
            <StatBadge
              label="Literacy Rate"
              value={stats.literacy ? `${stats.literacy}%` : '...'}
              icon="🎓"
            />
          </motion.div>
        </div>
      </div>

      {/* Feature Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Explore Kenya by Category
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Dive deep into any sector with detailed statistics, historical trends, and key indicators
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              to: "/population",
              icon: <FaUsers className="text-4xl text-indigo-600" />,
              title: "Population",
              description: "Demographics, growth rates, and population distribution across Kenya",
              color: "indigo",
              bg: "bg-indigo-50",
              border: "border-indigo-200",
              stats: stats.population ? `${stats.population}M people` : null
            },
            {
              to: "/economy",
              icon: <FaChartLine className="text-4xl text-green-600" />,
              title: "Economy",
              description: "GDP, inflation, employment, and key economic indicators",
              color: "green",
              bg: "bg-green-50",
              border: "border-green-200",
              stats: stats.gdp ? `${stats.gdp}% GDP growth` : null
            },
            {
              to: "/health",
              icon: <FaGlobeAfrica className="text-4xl text-red-600" />,
              title: "Health",
              description: "Healthcare access, disease prevalence, and health outcomes",
              color: "red",
              bg: "bg-red-50",
              border: "border-red-200",
              stats: stats.lifeExpectancy ? `${stats.lifeExpectancy} yrs life expectancy` : null
            },
            {
              to: "/education",
              icon: <FaDatabase className="text-4xl text-purple-600" />,
              title: "Education",
              description: "Enrollment rates, literacy, and education performance metrics",
              color: "purple",
              bg: "bg-purple-50",
              border: "border-purple-200",
              stats: stats.literacy ? `${stats.literacy}% literacy rate` : null
            }
          ].map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <Link to={card.to}>
                <div className={`bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition border ${card.border} h-full`}>
                  <div className={`${card.bg} w-16 h-16 rounded-xl flex items-center justify-center mb-4`}>
                    {card.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{card.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{card.description}</p>
                  {card.stats && (
                    <div className={`text-sm font-semibold text-${card.color}-600 flex items-center gap-1`}>
                      <FaCheckCircle /> {card.stats}
                    </div>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Counties Section */}
      <div className="bg-gradient-to-br from-gray-900 to-indigo-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Explore by County</h2>
            <p className="text-indigo-200 text-lg max-w-2xl mx-auto">
              Drill down into county-level data for all 47 counties across Kenya
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              <Link to="/counties/bomet">
                <div className="bg-white bg-opacity-10 rounded-xl p-6 border border-white border-opacity-20 hover:bg-opacity-20 transition">
                  <div className="text-4xl mb-3">📍</div>
                  <h3 className="text-xl font-bold mb-2">Bomet County</h3>
                  <p className="text-indigo-200 text-sm mb-4">South Rift Region · Tea farming hub</p>
                  <span className="text-xs bg-green-500 text-white px-3 py-1 rounded-full">Live Data</span>
                </div>
              </Link>
            </motion.div>

            <div className="bg-white bg-opacity-5 rounded-xl p-6 border border-white border-opacity-10">
              <div className="text-4xl mb-3 opacity-50">🗺️</div>
              <h3 className="text-xl font-bold mb-2 opacity-50">46 More Counties</h3>
              <p className="text-indigo-300 text-sm mb-4">Coming soon - all 47 counties will have dedicated data pages</p>
              <span className="text-xs bg-gray-600 text-gray-300 px-3 py-1 rounded-full">Coming Soon</span>
            </div>

            <div className="bg-white bg-opacity-5 rounded-xl p-6 border border-white border-opacity-10">
              <div className="text-4xl mb-3 opacity-50">⚖️</div>
              <h3 className="text-xl font-bold mb-2 opacity-50">County Comparison</h3>
              <p className="text-indigo-300 text-sm mb-4">Compare statistics across multiple counties side by side</p>
              <span className="text-xs bg-gray-600 text-gray-300 px-3 py-1 rounded-full">Coming Soon</span>
            </div>
          </div>
        </div>
      </div>

      {/* Why StatData */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Why StatData?</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Built to make Kenya's public data accessible to everyone
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              icon: "🔄",
              title: "Always Updated",
              description: "Data is automatically refreshed from official sources including KNBS, World Bank, and CBK"
            },
            {
              icon: "📊",
              title: "Visual & Clear",
              description: "Complex statistics presented in simple charts, tables, and visual comparisons"
            },
            {
              icon: "🔍",
              title: "Deep Insights",
              description: "Drill down from national to county level, compare years, and track trends over time"
            }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center p-6 bg-white rounded-xl shadow-md"
            >
              <div className="text-5xl mb-4">{item.icon}</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-600">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

    </div>
  );
}

function StatBadge({ label, value, icon }) {
  return (
    <div className="bg-white bg-opacity-10 rounded-xl p-4 text-center border border-white border-opacity-20">
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-indigo-200 text-sm">{label}</div>
    </div>
  );
}

export default Home;